import {useEffect, useState} from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Person } from "api/Community/CommunityInterfaces";

import GenericTable, {Column} from "components/Data/DataDisplays/GenericTable";
import ControlPanel from "components/Flow/Tabs/ItemMovementTabs/ControlPanel";

// 🔥 REQUIRED APIs for Add/Remove People Buttons
import { peopleAPI } from "api/Community/PeopleAPI";
import MoveConfirmDialog from "./ItemMovementTabs/MoveConfirmDialog";

interface PeopleMovementTabProps {
  nodeData: any | null;
  nameOfFunctionalityField?: string;
  nodeAPI: any;
  getNodePeople: (nodeId: number) => Promise<Person[]>;
  movePeopleBetweenNodes: (payload: any) => Promise<any>;

  // 🔥 Dynamic selectors
  inputTypeField?: string;
  outputTypeField?: string;
  inputLabelField?: string;
  outputLabelField?: string;
  
  // 🔥 Filter selectors
  inputFilterField?: string;
  outputFilterField?: string;

  includeMovementControls?: boolean;
}

export default function PeopleMovementTab({
  nodeData,
  nameOfFunctionalityField = "functionalities",
  nodeAPI,
  getNodePeople,
  movePeopleBetweenNodes,
  
  inputTypeField = "inputEdgeType",
  outputTypeField = "outputEdgeType",
  inputLabelField = "inputLabel",
  outputLabelField = "outputLabel",

  inputFilterField = "inputEdgeType",
  outputFilterField = "outputEdgeType",
  includeMovementControls = true,
}: PeopleMovementTabProps) {
  const navigate = useNavigate();

  const [people, setPeople] = useState<Person[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
  const [selectedInputID, setSelectedInput] = useState<string>("");
  const [selectedOutputID, setSelectedOutput] = useState<string>("");

  // 🔒 NEW: disable controls while moving
  const [moving, setMoving] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDirection, setPendingDirection] =
    useState<"forward" | "backward" | null>(null);


  useEffect(() => {
    if (!nodeData?.id) return;
    refreshPeople();
  }, [nodeData?.id]);

  
  // ---------------------------------------
  // LOAD INITIAL NODE PEOPLE + EDGES
  // ---------------------------------------

  if (!nodeData) {
    return <Typography>Select a node to edit</Typography>;
  }

  const functionalities = nodeData?.data[nameOfFunctionalityField] || [];

  const inputFunctionalities = functionalities.filter(
    (f: any) => f[inputFilterField] !== null && f[inputFilterField] !== undefined && f["inputEdgeType"] == 1 //people
  );

  const outputFunctionalities = functionalities.filter(
    (f: any) => f[outputFilterField] !== null && f[outputFilterField] !== undefined && f["outputEdgeType"] == 1 //people
  );


  // ---------------------------------------
  // REFRESH PEOPLE AFTER ANY CHANGE
  // ---------------------------------------
  const refreshPeople = async () => {
    const refreshed = await getNodePeople(nodeData.id);
    setPeople(refreshed);
    setSelectedPeople([]);
  };

  
  // ---------------------------------------
  // MOVE PEOPLE (SAFE + AWAITED)
  // ---------------------------------------
  const handleMove = (direction: "forward" | "backward") => {
    if (moving) return;
    if (selectedPeople.length === 0) return;

    const targetID =
      direction === "forward" ? selectedOutputID : selectedInputID;

    if (!targetID) return;

    setPendingDirection(direction);
    setConfirmOpen(true);
  };

  const confirmMove = async () => {
    if (!pendingDirection) return;

    setConfirmOpen(false);
    setMoving(true);

    try {
      const targetID =
        pendingDirection === "forward"
          ? selectedOutputID
          : selectedInputID;

      await movePeopleBetweenNodes({
        node_id: nodeData.id,
        handle_name: targetID,
        direction: pendingDirection,
        item_field_name: "people",
        selected_items: selectedPeople,
      });

      await refreshPeople();
    } catch (error) {
      console.error("Failed to move people:", error);
    } finally {
      setMoving(false);
      setPendingDirection(null);
    }
  };

  const cancelMove = () => {
    setConfirmOpen(false);
    setPendingDirection(null);
  };


  const columns: Column<any>[] = [
    { field: "first_name", label: "First Name", type: "text" },
    { field: "last_name", label: "Last Name", type: "text" },
    { field: "preferred_name", label: "Preferred Name", type: "text" },
    { field: "email", label: "Email", type: "text" },
    { field: "phone", label: "Phone", type: "text" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* ================= CONTROL PANEL ================= */}
      {includeMovementControls && (
        <ControlPanel
          inputFunctionalities={inputFunctionalities}
          outputFunctionalities={outputFunctionalities}

          selectedInputID={selectedInputID}
          selectedOutputID={selectedOutputID}
          setSelectedInput={setSelectedInput}
          setSelectedOutput={setSelectedOutput}

          handleMove={handleMove}
          disabled={moving}

          nodeData={nodeData}
          items={people}
          itemType = "People"
          selectedItems={selectedPeople}
          refreshItems={refreshPeople}
          nodeAPI={nodeAPI}
          itemAPI={peopleAPI}

          inputTypeField={inputTypeField}
          outputTypeField={outputTypeField}
          inputLabelField={inputLabelField}
          outputLabelField={outputLabelField}
        />
      )}

      {/* ================= PEOPLE TABLE ================= */}
      <GenericTable
        data={people}
        columns={columns}
        selectable={includeMovementControls && !moving}
        onSelectionChange={(rows) => setSelectedPeople(rows)}
        linkHandler={() => navigate("/community/people/")}
        includePaper={false}
      />
      <MoveConfirmDialog
        open={confirmOpen}
        onConfirm={confirmMove}
        onCancel={cancelMove}
      />

    </Box>
  );
}
